
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Image as ImageIcon, Lock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface DrillEditorProps {
  drillText: string;
  setDrillText: (text: string) => void;
  generatedImage: string | null;
  loading: boolean;
  onCreateDrill: () => void;
  selectedForConversion: boolean;
  setSelectedForConversion: (selected: boolean) => void;
  setGeneratedImage: (image: string | null) => void;
}

const DrillEditor = ({
  drillText,
  setDrillText,
  generatedImage,
  loading,
  onCreateDrill,
  selectedForConversion,
  setSelectedForConversion,
  setGeneratedImage,
}: DrillEditorProps) => {
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [title, setTitle] = useState("");
  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        setUserProfile(data);
      }
    };

    fetchUserProfile();
  }, []);

  const handleGenerateImage = async () => {
    if (!drillText.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter drill text before generating an image.",
      });
      return;
    }

    setIsGeneratingImage(true);
    try {
      console.log('Sending drill text:', drillText);
      const { data, error } = await supabase.functions.invoke('generate-drill-image', {
        body: { drillText }
      });

      if (error) throw error;

      if (data?.success && data?.image) {
        console.log('Image generated successfully');
        setGeneratedImage(data.image);
        toast({
          title: "Success",
          description: "Image generated successfully!",
        });
      } else {
        throw new Error(data?.error || "Failed to generate image");
      }
    } catch (error: any) {
      console.error('Image generation error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to generate image. Please try again.",
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Check if user is on free tier
  const isFreeTier = userProfile && !userProfile.paid_account;

  // Handle copy/paste restrictions for free tier
  const handlePaste = (e: React.ClipboardEvent) => {
    if (isFreeTier) {
      e.preventDefault();
      toast({
        variant: "destructive",
        title: "Feature Restricted",
        description: "Paste functionality is only available on paid plans.",
      });
    }
  };

  const handleCopy = (e: React.ClipboardEvent) => {
    if (isFreeTier) {
      e.preventDefault();
      toast({
        variant: "destructive",
        title: "Feature Restricted",
        description: "Copy functionality is only available on paid plans.",
      });
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (isFreeTier) {
      e.preventDefault();
      toast({
        variant: "destructive",
        title: "Feature Restricted",
        description: "Right-click menu is limited in the free tier.",
      });
    }
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border border-blue-100 shadow-lg">
      <CardHeader>
        <CardTitle>Create New Drill</CardTitle>
        <CardDescription>Type your drill instructions below</CardDescription>
        {isFreeTier && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md flex items-center text-amber-700 text-sm">
            <Lock className="h-4 w-4 mr-2" />
            Free tier limitations: No copy/paste, 2D preview only, 5 drills per month
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <Input
            placeholder="Enter drill title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mb-4"
            onPaste={handlePaste}
            onCopy={handleCopy}
            onContextMenu={handleContextMenu}
          />
        </div>
        <Tabs defaultValue="text" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="text">Text Editor</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="text">
            <Textarea
              placeholder="Type your drill instructions here..."
              className="min-h-[300px] resize-none"
              value={drillText}
              onChange={(e) => setDrillText(e.target.value)}
              onPaste={handlePaste}
              onCopy={handleCopy}
              onContextMenu={handleContextMenu}
            />
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="conversion"
                  checked={selectedForConversion}
                  onCheckedChange={(checked) => setSelectedForConversion(checked as boolean)}
                />
                <label
                  htmlFor="conversion"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Select for image conversion
                </label>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={handleGenerateImage}
                  disabled={isGeneratingImage || !selectedForConversion}
                >
                  {isGeneratingImage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Generate Image
                </Button>
                <Button onClick={() => {
                  if (!title.trim()) {
                    toast({
                      variant: "destructive",
                      title: "Error",
                      description: "Please enter a title for the drill.",
                    });
                    return;
                  }
                  onCreateDrill();
                }} disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Drill
                </Button>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="preview">
            <div className="min-h-[300px] p-4 border rounded-md">
              {generatedImage ? (
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold">{title}</h3>
                  <div className="w-full h-[300px] relative rounded-lg overflow-hidden bg-gray-50">
                    <img 
                      src={generatedImage} 
                      alt="Generated drill diagram" 
                      className="w-full h-full object-contain"
                    />
                    {isFreeTier && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2 text-center text-sm">
                        <Lock className="inline-block h-3 w-3 mr-1" />
                        Downloading images requires Pro or Enterprise plan
                      </div>
                    )}
                  </div>
                  <p className="text-gray-600 mt-4 whitespace-pre-wrap">{drillText}</p>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-4">
                  <ImageIcon className="h-12 w-12 opacity-20" />
                  <p>No preview available</p>
                  {drillText && (
                    <p className="text-sm text-center max-w-md">
                      Click "Generate Image" to create a visual representation of your drill
                    </p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default DrillEditor;
